import { IBufferControllerConfig } from '@bufferController/BufferController.config';
import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { Logger, TLogger } from '@logger/Logger';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { IVODFragment } from '@streamLoader/VODPlaylistRotationController/VODPlaylistRotationController';
import bind from '@utils/decorators/bind';

interface IFragmentTimeRange {
  startTS: number;
  endTS: number;
}
export class BufferCleaner extends ConfigConsumer<IBufferControllerConfig> {
  private _isPaused: boolean = false;
  private _isInVodMode: boolean = false;
  private _fragmentTimeRanges: IFragmentTimeRange[] = [];
  private _videoElement: HTMLVideoElement | null = null;

  private readonly _onBufferMetricsReadyListener: string;
  private readonly _onPeriodFragmentsFound: string;
  private readonly _onVODFragmentsInfoAvailableListener: string;
  private readonly _logger: TLogger;

  constructor(eventBus: TEventBus) {
    super('bufferController', eventBus);
    this._logger = Logger(eventBus, 'Doppio - BufferCleaner');

    this._logger = Logger(this._eventBus, 'Doppio - BufferCleaner');

    this._onBufferMetricsReadyListener = this._eventBus.subscribe(
      BufferControllerEvents.bufferMetricsAvailable,
      this._onBufferMetricsAvailable,
    );

    this._onPeriodFragmentsFound = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.fragments.periodFragmentsFound,
      this._onFragmentPeriodFound,
    );

    this._onVODFragmentsInfoAvailableListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlists.vodFragmentsInfo,
      this._onVODFragmentsInfoAvailable,
    );
  }

  @bind
  public destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onPeriodFragmentsFound);
    this._eventBus.unsubscribe(this._onVODFragmentsInfoAvailableListener);
    this._eventBus.unsubscribe(this._onBufferMetricsReadyListener);
  }

  public start(): void {
    this._isPaused = false;
  }

  public pause(): void {
    this._isPaused = true;
  }

  public setVODMode(isInVODMode: boolean): void {
    this._isInVodMode = isInVODMode;
  }

  public setVideoElement(videoElement: HTMLVideoElement): void {
    this._videoElement = videoElement;
  }

  public removeVideoElement(): void {
    this._videoElement = null;
  }

  @bind
  public _handleConfigUpdated(): void {}

  @bind
  private _onVODFragmentsInfoAvailable(fragments: IVODFragment[]): void {
    this._fragmentTimeRanges = fragments.map(({ fragmentStartTS, fragmentEndTS }) => ({
      startTS: fragmentStartTS,
      endTS: fragmentEndTS,
    }));
  }

  @bind
  private _onBufferMetricsAvailable(metrics: IBufferMetrics): void {
    if (this._isPaused || metrics.bufferedTimeRanges.length === 0) {
      return;
    }

    if (!this._isInVodMode || !this._fragmentTimeRanges.length) {
      const safeTimeRanges: IFragmentTimeRange[] = metrics.isPaused
        ? [
            { startTS: metrics.currentPlaybackPosition - 1, endTS: metrics.currentPlaybackPosition + 1 },
            {
              startTS: metrics.bufferedTimeRanges.slice(-1)[0]?.end - this.config.maxBackBufferAmount / 1000,
              endTS: metrics.bufferedTimeRanges.slice(-1)[0]?.end,
            },
          ]
        : [
            {
              startTS: metrics.currentPlaybackPosition - this.config.maxBackBufferAmount / 1000,
              endTS: metrics.bufferedTimeRanges.slice(-1)[0].end,
            },
          ];
      this._clearBufferWithSafetyTimeRanges(safeTimeRanges);
    }
  }

  @bind
  private _onFragmentPeriodFound({ endTsOfPeriod }: { startTsOfPeriod: number; endTsOfPeriod: number }): void {
    if (this._isPaused || !this._videoElement || this._videoElement.currentTime < this.config.maxBackBufferAmount / 1000) {
      return;
    }

    const { currentTime } = this._videoElement;

    const targetMinimalBuffer: number = currentTime - this.config.maxBackBufferAmount / 1000;

    const safetyMinimalBufferRanges: IFragmentTimeRange[] = [{ startTS: targetMinimalBuffer, endTS: endTsOfPeriod }];

    this._clearBufferWithSafetyTimeRanges(safetyMinimalBufferRanges);
  }

  private _clearBufferWithSafetyTimeRanges(safetyTimeRanges: IFragmentTimeRange[]): void {
    if (!this._videoElement?.buffered.length) {
      return;
    }

    const rangesToClear: IFragmentTimeRange[] = safetyTimeRanges
      .sort((a, b) => a.startTS - b.startTS)
      .filter(({ startTS, endTS }) => startTS < endTS)
      .map((range, index) => ({
        startTS: index > 0 ? safetyTimeRanges[index - 1].endTS : 0,
        endTS: range.startTS,
      }))
      .filter(({ startTS, endTS }) => startTS < endTS);

    if (
      this._isInVodMode &&
      Number.isFinite(this._videoElement.duration) &&
      safetyTimeRanges.length &&
      safetyTimeRanges.slice(-1)[0].endTS < this._videoElement.duration
    ) {
      rangesToClear.push({ startTS: safetyTimeRanges.slice(-1)[0].endTS, endTS: this._videoElement.duration });
    }

    rangesToClear.forEach((range) => {
      this._eventBus.publish(BufferControllerEvents.requestSourceBufferOperation, (sourceBuffer: SourceBuffer) => {
        this._logger.verbose(`Removing buffered range ${range.startTS} -> ${range.endTS}`);
        sourceBuffer.remove(range.startTS, range.endTS);
      });
    });
  }
}
